import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {
  Cog6ToothIcon,
  UserIcon,
  BellIcon,
  ShieldCheckIcon,
  EyeSlashIcon,
  GlobeAltIcon,
  PaintBrushIcon,
  CreditCardIcon,
  QuestionMarkCircleIcon,
  ArrowRightOnRectangleIcon,
  TrashIcon,
  ChatBubbleLeftIcon,
  BookOpenIcon
} from '@heroicons/react/24/outline'

const SettingsPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState('profile')
  const [notifications, setNotifications] = useState({
    matches: true,
    messages: true,
    likes: true,
    stories: false,
    marketing: false
  })

  const sections = [
    { id: 'profile', name: 'Profile', icon: UserIcon },
    { id: 'notifications', name: 'Notifications', icon: BellIcon },
    { id: 'privacy', name: 'Privacy & Security', icon: ShieldCheckIcon },
    { id: 'appearance', name: 'Appearance', icon: PaintBrushIcon },
    { id: 'billing', name: 'Billing', icon: CreditCardIcon },
    { id: 'help', name: 'Help & Support', icon: QuestionMarkCircleIcon }
  ]

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center space-x-4 mb-2">
          <Cog6ToothIcon className="h-10 w-10 text-primary-400" />
          <h1 className="text-4xl font-display font-bold">Settings</h1>
        </div>
        <p className="text-white/70">Manage your account preferences and settings</p>
      </motion.div>

      <div className="grid lg:grid-cols-4 gap-8">
        {/* Sidebar Navigation */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="lg:col-span-1"
        >
          <div className="card-glass p-4 space-y-2">
            {sections.map((section) => {
              const Icon = section.icon
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                    activeSection === section.id
                      ? 'bg-gradient-to-r from-primary-600/30 to-secondary-600/30 text-white'
                      : 'text-white/70 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{section.name}</span>
                </button>
              )
            })}
          </div>
        </motion.div>

        {/* Content Area */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="lg:col-span-3"
        >
          <div className="card-glass p-8 space-y-8">
            {activeSection === 'profile' && (
              <ProfileSettings />
            )}
            {activeSection === 'notifications' && (
              <NotificationSettings 
                notifications={notifications}
                setNotifications={setNotifications}
              />
            )}
            {activeSection === 'privacy' && (
              <PrivacySettings />
            )}
            {activeSection === 'appearance' && (
              <AppearanceSettings />
            )}
            {activeSection === 'billing' && (
              <BillingSettings />
            )}
            {activeSection === 'help' && (
              <HelpSettings />
            )}
          </div>
        </motion.div>
      </div>
    </div>
  )
}

const ProfileSettings: React.FC = () => (
  <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-bold mb-4">Profile Information</h2>
      <p className="text-white/70 mb-6">Update your personal information and profile details</p>
    </div>

    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <label className="block text-sm font-medium mb-2">First Name</label>
        <input
          type="text"
          className="input-glass w-full"
          placeholder="John"
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-2">Last Name</label>
        <input
          type="text"
          className="input-glass w-full"
          placeholder="Doe"
        />
      </div>
    </div>

    <div>
      <label className="block text-sm font-medium mb-2">Email</label>
      <input
        type="email"
        className="input-glass w-full"
        placeholder="john@example.com"
      />
    </div>

    <div>
      <label className="block text-sm font-medium mb-2">Bio</label>
      <textarea
        className="input-glass w-full h-32"
        placeholder="Tell us about yourself..."
      />
    </div>

    <div>
      <label className="block text-sm font-medium mb-2">Location</label>
      <input
        type="text"
        className="input-glass w-full"
        placeholder="New York, USA"
      />
    </div>

    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className="btn-primary"
    >
      Save Changes
    </motion.button>
  </div>
)

interface NotificationSettingsProps {
  notifications: {
    matches: boolean
    messages: boolean
    likes: boolean
    stories: boolean
    marketing: boolean
  }
  setNotifications: React.Dispatch<React.SetStateAction<{
    matches: boolean
    messages: boolean
    likes: boolean
    stories: boolean
    marketing: boolean
  }>>
}

const NotificationSettings: React.FC<NotificationSettingsProps> = ({ notifications, setNotifications }) => {
  const toggleNotification = (key: keyof typeof notifications) => {
    setNotifications(prev => ({ ...prev, [key]: !prev[key] }))
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4">Notifications</h2>
        <p className="text-white/70 mb-6">Choose what notifications you want to receive</p>
      </div>

      <div className="space-y-4">
        <NotificationToggle
          label="New Matches"
          description="Get notified when someone likes you back"
          checked={notifications.matches}
          onChange={() => toggleNotification('matches')}
        />
        <NotificationToggle
          label="Messages"
          description="Receive notifications for new messages"
          checked={notifications.messages}
          onChange={() => toggleNotification('messages')}
        />
        <NotificationToggle
          label="Likes"
          description="Get notified when someone likes your profile"
          checked={notifications.likes}
          onChange={() => toggleNotification('likes')}
        />
        <NotificationToggle
          label="Story Updates"
          description="Notifications for new stories from your connections"
          checked={notifications.stories}
          onChange={() => toggleNotification('stories')}
        />
        <NotificationToggle
          label="Marketing Emails"
          description="Receive updates about new features and promotions"
          checked={notifications.marketing}
          onChange={() => toggleNotification('marketing')}
        />
      </div>
    </div>
  )
}

const PrivacySettings: React.FC = () => (
  <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-bold mb-4">Privacy & Security</h2>
      <p className="text-white/70 mb-6">Manage your privacy settings and account security</p>
    </div>

    <div className="space-y-4">
      <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
        <div className="flex items-center space-x-3">
          <EyeSlashIcon className="h-6 w-6 text-primary-400" />
          <div>
            <p className="font-medium">Incognito Mode</p>
            <p className="text-sm text-white/70">Browse profiles privately</p>
          </div>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input type="checkbox" className="sr-only peer" />
          <div className="w-11 h-6 bg-white/20 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
        </label>
      </div>

      <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
        <div className="flex items-center space-x-3">
          <GlobeAltIcon className="h-6 w-6 text-primary-400" />
          <div>
            <p className="font-medium">Show Distance</p>
            <p className="text-sm text-white/70">Display distance on your profile</p>
          </div>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input type="checkbox" className="sr-only peer" defaultChecked />
          <div className="w-11 h-6 bg-white/20 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
        </label>
      </div>
    </div>

    <div className="pt-6 border-t border-white/10">
      <h3 className="text-xl font-bold mb-4">Account Actions</h3>
      <div className="space-y-3">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="btn-glass w-full flex items-center justify-center space-x-2"
        >
          <ShieldCheckIcon className="h-5 w-5" />
          <span>Change Password</span>
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="btn-glass w-full flex items-center justify-center space-x-2 text-orange-400 border-orange-400/30"
        >
          <ArrowRightOnRectangleIcon className="h-5 w-5" />
          <span>Sign Out</span>
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="btn-glass w-full flex items-center justify-center space-x-2 text-red-400 border-red-400/30"
        >
          <TrashIcon className="h-5 w-5" />
          <span>Delete Account</span>
        </motion.button>
      </div>
    </div>
  </div>
)

const AppearanceSettings: React.FC = () => (
  <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-bold mb-4">Appearance</h2>
      <p className="text-white/70 mb-6">Customize how ConnectHub looks</p>
    </div>

    <div>
      <h3 className="font-medium mb-4">Theme</h3>
      <div className="grid grid-cols-3 gap-4">
        <ThemeOption name="Dark" active={true} />
        <ThemeOption name="Light" active={false} />
        <ThemeOption name="Auto" active={false} />
      </div>
    </div>

    <div>
      <h3 className="font-medium mb-4">Accent Color</h3>
      <div className="grid grid-cols-6 gap-4">
        <ColorOption color="bg-purple-500" active={true} />
        <ColorOption color="bg-blue-500" active={false} />
        <ColorOption color="bg-pink-500" active={false} />
        <ColorOption color="bg-orange-500" active={false} />
        <ColorOption color="bg-green-500" active={false} />
        <ColorOption color="bg-red-500" active={false} />
      </div>
    </div>
  </div>
)

const BillingSettings: React.FC = () => (
  <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-bold mb-4">Billing & Subscription</h2>
      <p className="text-white/70 mb-6">Manage your payment methods and subscriptions</p>
    </div>

    <div className="p-6 bg-gradient-to-r from-primary-600/20 to-secondary-600/20 rounded-xl border border-primary-500/30">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-xl font-bold">Premium Plan</h3>
          <p className="text-white/70">$19.99/month</p>
        </div>
        <span className="px-4 py-2 bg-primary-500 rounded-full text-sm font-medium">Active</span>
      </div>
      <p className="text-sm text-white/70 mb-4">Next billing date: January 15, 2027</p>
      <div className="flex space-x-3">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="btn-glass"
        >
          Change Plan
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="btn-glass text-red-400"
        >
          Cancel Subscription
        </motion.button>
      </div>
    </div>

    <div>
      <h3 className="font-medium mb-4">Payment Methods</h3>
      <div className="space-y-3">
        <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
          <div className="flex items-center space-x-3">
            <CreditCardIcon className="h-6 w-6 text-primary-400" />
            <div>
              <p className="font-medium">•••• •••• •••• 4242</p>
              <p className="text-sm text-white/70">Expires 12/25</p>
            </div>
          </div>
          <button className="text-sm text-primary-400 hover:underline">Edit</button>
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="btn-glass w-full"
        >
          Add Payment Method
        </motion.button>
      </div>
    </div>
  </div>
)

const HelpSettings: React.FC = () => (
  <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-bold mb-4">Help & Support</h2>
      <p className="text-white/70 mb-6">Get help and learn more about ConnectHub</p>
    </div>

    <div className="grid md:grid-cols-2 gap-4">
      <HelpCard
        icon={QuestionMarkCircleIcon}
        title="FAQ"
        description="Find answers to common questions"
      />
      <HelpCard
        icon={ChatBubbleLeftIcon}
        title="Contact Support"
        description="Get help from our team"
      />
      <HelpCard
        icon={BookOpenIcon}
        title="User Guide"
        description="Learn how to use ConnectHub"
      />
      <HelpCard
        icon={ShieldCheckIcon}
        title="Safety Center"
        description="Tips for staying safe online"
      />
    </div>

    <div className="pt-6 border-t border-white/10">
      <h3 className="font-medium mb-4">About</h3>
      <div className="space-y-2 text-white/70">
        <p>Version 2.0.0</p>
        <p>© 2026 ConnectHub. All rights reserved.</p>
        <div className="flex space-x-4 pt-2">
          <a href="#" className="text-primary-400 hover:underline">Terms of Service</a>
          <a href="#" className="text-primary-400 hover:underline">Privacy Policy</a>
          <a href="#" className="text-primary-400 hover:underline">Community Guidelines</a>
        </div>
      </div>
    </div>
  </div>
)

interface NotificationToggleProps {
  label: string
  description: string
  checked: boolean
  onChange: () => void
}

const NotificationToggle: React.FC<NotificationToggleProps> = ({ label, description, checked, onChange }) => (
  <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
    <div>
      <p className="font-medium">{label}</p>
      <p className="text-sm text-white/70">{description}</p>
    </div>
    <label className="relative inline-flex items-center cursor-pointer">
      <input type="checkbox" className="sr-only peer" checked={checked} onChange={onChange} />
      <div className="w-11 h-6 bg-white/20 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
    </label>
  </div>
)

const ThemeOption: React.FC<{ name: string; active: boolean }> = ({ name, active }) => (
  <div className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
    active ? 'border-primary-500 bg-primary-500/20' : 'border-white/10 hover:border-white/30'
  }`}>
    <p className="font-medium text-center">{name}</p>
  </div>
)

const ColorOption: React.FC<{ color: string; active: boolean }> = ({ color, active }) => (
  <div className={`${color} h-12 rounded-xl cursor-pointer transition-all ${
    active ? 'ring-4 ring-white/50 scale-110' : 'hover:scale-105'
  }`} />
)

const HelpCard: React.FC<{ icon: React.ComponentType<any>; title: string; description: string }> = ({ icon: Icon, title, description }) => (
  <motion.div
    whileHover={{ scale: 1.03 }}
    className="p-6 bg-white/5 rounded-xl cursor-pointer hover:bg-white/10 transition-colors"
  >
    <Icon className="h-8 w-8 text-primary-400 mb-3" />
    <h3 className="font-bold mb-1">{title}</h3>
    <p className="text-sm text-white/70">{description}</p>
  </motion.div>
)

export default SettingsPage
